import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFileLines,
    faUsers,
    faLightbulb,
    faChevronDown,
    faChevronUp,
    faEdit,
    faEye,
    faSpinner,
    faRotateRight
} from '@fortawesome/free-solid-svg-icons';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { contentService } from '../../services/contentService';
import LoadingSpinner from '../Common/LoadingSpinner';
import './ContentManagement.css';

// Configure Quill for content rich text editing
const Quill = ReactQuill.Quill;

// Use style-based attributors for fonts and sizes
const SizeStyle = Quill.import('attributors/style/size');
SizeStyle.whitelist = ['10pt', '10.5pt', '12pt', '14pt', '18pt'];
Quill.register(SizeStyle, true);

const FontStyle = Quill.import('attributors/style/font');
FontStyle.whitelist = ['arial', 'times-new-roman', 'calibri', 'helvetica', 'georgia', 'verdana'];
Quill.register(FontStyle, true);

// Configure Quill modules for content editing - comprehensive tools
const quillModules = {
    toolbar: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link'],
        ['clean']
    ],
    clipboard: {
        matchVisual: false
    }
};

const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'align', 'link'
];

function ContentManagement({ currentUser }) {
    const { user } = useAuth0();
    
    // State management
    const [contentItems, setContentItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [expandedSections, setExpandedSections] = useState({});
    const [editingContent, setEditingContent] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    
    // Fetch content on component mount
    useEffect(() => {
        fetchContent();
    }, []);

    const fetchContent = async () => {
        setIsRefreshing(true);
        try {
            const content = await contentService.getAllContent();
            setContentItems(content);
            
            
        } catch (error) {
            console.error('Error fetching content:', error);
            toast.error('Failed to load content');
        } finally {
            setIsRefreshing(false);
            setIsLoading(false);
        }
    };

    // Define content sections with their PageLookupId mappings (from SharePoint lookup field)
    const contentSections = [
        {
            id: 'home',
            title: 'Home Page',
            icon: faFileLines,
            description: 'Manage home page content',
            disabled: false,
            pageLookupIds: ['1'] // Daire Learn More
        },
        {
            id: 'policy',
            title: 'Policy & Regulatory',
            icon: faFileLines,
            description: 'Manage Policy & Regulatory content',
            disabled: false,
            pageLookupIds: ['2'] // Consultations Blob, Quarterly Update Blob, CBI Update Blob
        },
        {
            id: 'capability',
            title: 'Capability & Innovation',
            icon: faLightbulb,
            description: 'Manage Capability & Innovation content',
            disabled: false,
            pageLookupIds: ['8'] // About Capabilities, About Innovation Radar, etc.
        },
        {
            id: 'workinggroups',
            title: 'Working Groups',
            icon: faUsers,
            description: 'Manage Working Groups content',
            disabled: false,
            pageLookupIds: ['5'] // Intro, More Info Content
        },
        {
            id: 'council',
            title: 'Council Member',
            icon: faUsers,
            description: 'Manage Council Member content',
            disabled: true,
            pageLookupIds: [] // Enable when council content exists
        }
    ];

    // Filter content items by section using PageLookupId
    const getContentForSection = (section) => {
        // Simple and clean: filter by PageLookupId!
        return contentItems.filter(item => 
            section.pageLookupIds.includes(item.PageLookupId)
        );
    };

    // Toggle section expansion
    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    // Handle content editing
    const handleEditContent = (contentItem) => {
        setEditingContent(contentItem);
        setEditModalOpen(true);
    };

    // Calculate minutes until next 5-minute boundary for cache refresh
    const getMinutesUntilNextSync = () => {
        const now = new Date();
        const minutes = now.getMinutes();
        const seconds = now.getSeconds();
        
        // Find next 5-minute boundary (0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55)
        const nextBoundary = Math.ceil(minutes / 5) * 5;
        let minutesUntilSync = nextBoundary - minutes;
        
        // If we're past 55 minutes, next boundary is 0 minutes of next hour
        if (nextBoundary >= 60) {
            minutesUntilSync = 60 - minutes;
        }
        
        // If we're exactly on a boundary and have seconds, add 1 minute
        if (minutesUntilSync === 0 && seconds > 0) {
            minutesUntilSync = 5;
        }
        
        // Minimum 1 minute to be safe
        return Math.max(minutesUntilSync, 1);
    };

    const handleUpdateContent = async (contentData) => {
        try {
            await contentService.updateContent(editingContent.ID, contentData, user?.name);
            
            const minutesToWait = getMinutesUntilNextSync();
            const waitMessage = minutesToWait === 1 
                ? 'Content updated successfully! Changes will be live on the member portal in 1 minute.'
                : `Content updated successfully! Changes will be live on the member portal in ${minutesToWait} minutes.`;
            
            toast.success(waitMessage, {
                autoClose: 6000, // Show longer for this important message
                hideProgressBar: false
            });
            
            setEditModalOpen(false);
            setEditingContent(null);
            await fetchContent(); // Refresh content
        } catch (error) {
            console.error('Error updating content:', error);
            toast.error('Failed to update content');
        }
    };

    // Render content items for a section
    const renderSectionContent = (section) => {
        if (section.disabled) {
            return (
                <div className="content-mgmt-section">
                    <p>🚧 This section is coming soon! Content management tools are in development.</p>
                </div>
            );
        }

        const sectionContent = getContentForSection(section);

        if (sectionContent.length === 0) {
            return (
                <div className="content-mgmt-section">
                    <p>No content items found for this section.</p>
                    <div className="content-mgmt-section-actions">
                        <button className="content-mgmt-btn secondary" onClick={fetchContent}>
                            <FontAwesomeIcon icon={faRotateRight} /> Refresh
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div className="content-mgmt-section">
                <p>Manage {section.title.toLowerCase()} content for the member portal.</p>
                <div className="content-mgmt-items-list">
                    {sectionContent.map(item => (
                        <div key={item.ID} className="content-mgmt-item-card">
                            <div className="content-mgmt-item-header">
                                <h4>{item.Title || `Content Item ${item.ID}`}</h4>
                                <div className="content-mgmt-item-status">
                                    <span className={`content-mgmt-status-badge ${item.Publish ? 'published' : 'draft'}`}>
                                        {item.Publish ? 'Published' : 'Draft'}
                                    </span>
                                </div>
                            </div>
                            <div className="content-mgmt-item-preview">
                                {item.Content ? (
                                    <div 
                                        className="content-mgmt-content-preview"
                                        dangerouslySetInnerHTML={{ 
                                            __html: item.Content.substring(0, 150) + (item.Content.length > 150 ? '...' : '')
                                        }}
                                    />
                                ) : (
                                    <p className="content-mgmt-no-content">No content available</p>
                                )}
                            </div>
                            <div className="content-mgmt-item-actions">
                                <button 
                                    className="content-mgmt-btn primary"
                                    onClick={() => handleEditContent(item)}
                                >
                                    <FontAwesomeIcon icon={faEdit} /> Edit
                                </button>
                                <button className="content-mgmt-btn secondary">
                                    <FontAwesomeIcon icon={faEye} /> Preview
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Get statistics
    const getStats = () => {
        const publishedCount = contentItems.filter(item => item.Publish).length;
        const draftCount = contentItems.filter(item => !item.Publish).length;
        const activeSections = contentSections.filter(s => !s.disabled).length;
        
        return {
            total: contentItems.length,
            published: publishedCount,
            draft: draftCount,
            activeSections
        };
    };

    const stats = getStats();

    if (isLoading) {
        return (
            <div className="content-mgmt-loading-container">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="content-mgmt-management">
            {/* Header Controls */}
            <div className="content-mgmt-controls">
                <button 
                    className="content-mgmt-control-btn secondary"
                    onClick={fetchContent}
                    disabled={isRefreshing}
                >
                    <FontAwesomeIcon 
                        icon={faRotateRight} 
                        spin={isRefreshing}
                    /> 
                    {isRefreshing ? 'Refreshing...' : 'Refresh'}
                </button>
            </div>

            {/* Statistics Overview */}
            <div className="content-mgmt-overview">
                <div className="content-mgmt-stat-card">
                    <span>{stats.activeSections}</span>
                    <label>Active Sections</label>
                </div>
                <div className="content-mgmt-stat-card">
                    <span>{stats.published}</span>
                    <label>Published</label>
                </div>
                <div className="content-mgmt-stat-card">
                    <span>{stats.draft}</span>
                    <label>Draft</label>
                </div>
                <div className="content-mgmt-stat-card">
                    <span>{stats.total}</span>
                    <label>Total Items</label>
                </div>
            </div>

            {/* Content Sections */}
            <div className="content-mgmt-accordion">
                {contentSections.map((section, index) => {
                    const isExpanded = expandedSections[section.id];
                    const sectionContentItems = getContentForSection(section);
                    
                    return (
                        <div 
                            key={index} 
                            className={`content-mgmt-item ${section.disabled ? 'disabled' : ''} ${
                                isExpanded ? 'expanded' : ''
                            }`}
                        >
                            <div 
                                className={`content-mgmt-header ${section.disabled ? 'disabled' : ''}`}
                                onClick={() => !section.disabled && toggleSection(section.id)}
                            >
                                <div className="content-mgmt-title">
                                    <FontAwesomeIcon icon={section.icon} className="content-mgmt-icon" />
                                    <div className="content-mgmt-title-text">
                                        <h3>{section.title}</h3>
                                        <p className="content-mgmt-description">{section.description}</p>
                                    </div>
                                </div>
                                <div className="content-mgmt-actions">
                                    {!section.disabled && (
                                        <span className="content-mgmt-badge">
                                            {sectionContentItems.length} items
                                        </span>
                                    )}
                                    {section.disabled && (
                                        <span className="content-mgmt-badge disabled">Coming Soon</span>
                                    )}
                                    {!section.disabled && (
                                        <FontAwesomeIcon 
                                            icon={isExpanded ? faChevronUp : faChevronDown} 
                                            className="content-mgmt-expand-icon"
                                        />
                                    )}
                                </div>
                            </div>
                            {isExpanded && (
                                <div className="content-mgmt-content">
                                    {renderSectionContent(section)}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Edit Modal */}
            {editModalOpen && editingContent && (
                <ContentEditModal
                    content={editingContent}
                    onSave={handleUpdateContent}
                    onClose={() => {
                        setEditModalOpen(false);
                        setEditingContent(null);
                    }}
                />
            )}
        </div>
    );
}

// Simple Content Edit Modal Component
const ContentEditModal = ({ content, onSave, onClose }) => {
    const [formData, setFormData] = useState({
        Title: content.Title || '',
        Content: content.Content || '',
        Publish: content.Publish !== undefined ? content.Publish : true
    });
    const [isSaving, setIsSaving] = useState(false);

    const handleContentChange = (content) => {
        setFormData(prev => ({
            ...prev,
            Content: content
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            await onSave(formData);
        } catch (error) {
            // Error handling is done in parent component
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="content-mgmt-modal-overlay">
            <div className="content-mgmt-modal-content content-mgmt-edit-modal">
                <div className="content-mgmt-modal-header">
                    <h3>Edit Content</h3>
                    <button className="content-mgmt-close-btn" onClick={onClose}>×</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="content-mgmt-form-group">
                        <label>Title</label>
                        <input
                            type="text"
                            value={formData.Title}
                            onChange={(e) => setFormData(prev => ({ ...prev, Title: e.target.value }))}
                            placeholder="Content title"
                        />
                    </div>
                    <div className="content-mgmt-form-group">
                        <label>Content</label>
                        <div className="content-mgmt-rich-text-editor">
                            <ReactQuill
                                value={formData.Content || ''}
                                onChange={handleContentChange}
                                modules={quillModules}
                                formats={quillFormats}
                                placeholder="Enter content body with rich text formatting..."
                                style={{ height: '300px', marginBottom: '50px' }}
                            />
                        </div>
                    </div>
                    <div className="content-mgmt-form-group">
                        <label className="content-mgmt-checkbox-label">
                            <input
                                type="checkbox"
                                checked={formData.Publish}
                                onChange={(e) => setFormData(prev => ({ ...prev, Publish: e.target.checked }))}
                            />
                            Published
                        </label>
                    </div>
                    <div className="content-mgmt-modal-actions">
                        <button type="button" onClick={onClose} className="content-mgmt-btn-secondary">
                            Cancel
                        </button>
                        <button type="submit" disabled={isSaving} className="content-mgmt-btn-primary">
                            {isSaving ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                                </>
                            ) : (
                                'Save Changes'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ContentManagement; 